import { motion } from 'framer-motion';
import { useState } from 'react';
import { useParams, Link } from 'wouter';
import { useForm, useFieldArray } from 'react-hook-form';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  ArrowLeft,
  Trash2, 
  BarChart3, 
  Users, 
  Check,
  X,
  Eye,
  Clock,
  Search,
  MapPin,
  Plus,
  Save,
  Archive,
  ArchiveRestore,
  GripVertical
} from 'lucide-react';
import { 
  useAdminPoll,
  useUpdatePoll, 
  useDeletePoll,
  usePreviewPollTarget,
  useSearchCities
} from '@/lib/api';
import { PollTargetCriteria } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import Markdown from '@/components/Markdown';
import { MarkdownTextarea } from '../components/MarkdownTextarea';
import { useConfirm } from '@/components/ConfirmModal';
import { Loader } from '@/components/Loader';

// Sortable Answer Row Component
interface SortableAnswerRowProps {
  id: string;
  index: number;
  register: any;
  canDelete: boolean;
  onDelete: () => void;
}

function SortableAnswerRow({ id, index, register, canDelete, onDelete }: SortableAnswerRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-center">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-dark-muted hover:text-primary-500 transition-colors"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <input
        {...register(`answers.${index}.text`)}
        className="input flex-1"
        placeholder={`Answer ${index + 1}`}
      />
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-ghost text-red-400 px-3"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

interface PollFormData {
  title: string;
  question: string;
  answers: { id?: string; text: string }[];
  publicResults: boolean;
  status: 'draft' | 'active' | 'closed' | 'archived';
  startsAt: string;
  endsAt: string;
  subscribersOnly: boolean;
  notSubscribed: boolean;
  hasSpentMoney: boolean;
  cities: string[];
  quizAnswerRegex: { field: string; pattern: string }[];
}

export default function AdminPollDetail() {
  const { pollId } = useParams<{ pollId: string }>();
  const [targetPreviewCount, setTargetPreviewCount] = useState<number | null>(null);
  const [showCitySearch, setShowCitySearch] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [newCityId, setNewCityId] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { data: pollData, isLoading, refetch } = useAdminPoll(pollId);
  const { data: citiesData } = useSearchCities(citySearchQuery);
  const updatePoll = useUpdatePoll();
  const deletePoll = useDeletePoll();
  const previewTarget = usePreviewPollTarget();
  const toast = useToast();
  const { confirm } = useConfirm();

  const cities = citiesData?.data || [];
  const poll = pollData?.data;

  const { register, handleSubmit, reset, watch, setValue, control } = useForm<PollFormData>();

  const { fields: answerFields, append: appendAnswer, remove: removeAnswer, move: moveAnswer } = useFieldArray({
    control,
    name: 'answers',
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = answerFields.findIndex((field) => field.id === active.id);
      const newIndex = answerFields.findIndex((field) => field.id === over.id);
      moveAnswer(oldIndex, newIndex);
    }
  };

  const { fields: regexFields, append: appendRegex, remove: removeRegex } = useFieldArray({
    control,
    name: 'quizAnswerRegex',
  });

  const startEditing = () => {
    if (!poll) return;
    reset({
      title: poll.title,
      question: poll.question,
      answers: poll.answers.map(a => ({ id: a.id, text: a.text })),
      publicResults: poll.publicResults,
      status: poll.status,
      startsAt: poll.startsAt ? new Date(poll.startsAt).toISOString().slice(0, 16) : '',
      endsAt: poll.endsAt ? new Date(poll.endsAt).toISOString().slice(0, 16) : '',
      subscribersOnly: poll.targetCriteria?.subscribersOnly || false,
      notSubscribed: poll.targetCriteria?.notSubscribed || false,
      hasSpentMoney: poll.targetCriteria?.hasSpentMoney || false,
      cities: poll.targetCriteria?.cities || [],
      quizAnswerRegex: poll.targetCriteria?.quizAnswerRegex || [],
    });
    setIsEditing(true);
    
    // Preview target
    const criteria: PollTargetCriteria = {};
    if (poll.targetCriteria?.subscribersOnly) criteria.subscribersOnly = true;
    if (poll.targetCriteria?.notSubscribed) criteria.notSubscribed = true;
    if (poll.targetCriteria?.hasSpentMoney) criteria.hasSpentMoney = true;
    if (poll.targetCriteria?.cities?.length) criteria.cities = poll.targetCriteria.cities;
    if (poll.targetCriteria?.quizAnswerRegex?.length) criteria.quizAnswerRegex = poll.targetCriteria.quizAnswerRegex;
    
    previewTarget.mutate(criteria, {
      onSuccess: (data) => setTargetPreviewCount(data.data?.count ?? null),
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = async (data: PollFormData) => {
    if (!poll) return;

    const targetCriteria: PollTargetCriteria = {};
    if (data.subscribersOnly) targetCriteria.subscribersOnly = true;
    if (data.notSubscribed) targetCriteria.notSubscribed = true;
    if (data.hasSpentMoney) targetCriteria.hasSpentMoney = true;
    if (data.cities && data.cities.length > 0) targetCriteria.cities = data.cities;
    if (data.quizAnswerRegex && data.quizAnswerRegex.length > 0) {
      const validRegex = data.quizAnswerRegex.filter(r => r.field && r.pattern);
      if (validRegex.length > 0) targetCriteria.quizAnswerRegex = validRegex;
    }

    updatePoll.mutate(
      { 
        id: poll.id, 
        data: { 
          title: data.title,
          question: data.question,
          answers: data.answers.filter(a => a.text.trim()),
          publicResults: data.publicResults,
          status: data.status,
          targetCriteria: Object.keys(targetCriteria).length > 0 ? targetCriteria : null,
          startsAt: data.startsAt || null,
          endsAt: data.endsAt || null,
        } 
      },
      {
        onSuccess: () => {
          toast.success('Poll updated successfully');
          setIsEditing(false);
          refetch();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to update poll');
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!poll) return;
    
    const confirmed = await confirm({
      title: 'Delete Poll',
      message: `Are you sure you want to delete "${poll.title}"? This will also delete all responses.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });

    if (confirmed) {
      deletePoll.mutate(poll.id, {
        onSuccess: () => {
          toast.success('Poll deleted successfully');
          window.history.back();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to delete poll');
        },
      });
    }
  };

  const handleStatusChange = async (newStatus: 'draft' | 'active' | 'closed' | 'archived') => {
    if (!poll) return;
    
    updatePoll.mutate(
      { id: poll.id, data: { status: newStatus } },
      {
        onSuccess: () => {
          toast.success(`Poll ${newStatus === 'active' ? 'activated' : newStatus === 'closed' ? 'closed' : newStatus === 'archived' ? 'archived' : 'set to draft'}`);
          refetch();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to update poll status');
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400">Active</span>;
      case 'closed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400">Closed</span>;
      case 'archived':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-500/20 text-purple-400">Archived</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-400">Draft</span>;
    }
  };

  // Update preview when criteria changes during editing
  const updatePreview = () => {
    const criteria: PollTargetCriteria = {};
    const subscribersOnly = watch('subscribersOnly');
    const notSubscribed = watch('notSubscribed');
    const hasSpentMoney = watch('hasSpentMoney');
    const selectedCities = watch('cities');
    const quizRegex = watch('quizAnswerRegex');
    
    if (subscribersOnly) criteria.subscribersOnly = true;
    if (notSubscribed) criteria.notSubscribed = true;
    if (hasSpentMoney) criteria.hasSpentMoney = true;
    if (selectedCities && selectedCities.length > 0) criteria.cities = selectedCities;
    if (quizRegex && quizRegex.length > 0 && quizRegex.some(r => r.field && r.pattern)) {
      criteria.quizAnswerRegex = quizRegex.filter(r => r.field && r.pattern);
    }

    previewTarget.mutate(criteria, {
      onSuccess: (data) => setTargetPreviewCount(data.data?.count ?? null),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link href="/polls" className="btn-ghost mb-6 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Polls
        </Link>
        <div className="card text-center py-12">
          <BarChart3 className="w-16 h-16 text-dark-muted mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Poll not found</h3>
          <p className="text-dark-muted mb-6">
            The poll you're looking for doesn't exist.
          </p>
          <Link href="/polls" className="btn btn-primary">
            Go to Polls
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-6"
      >
        <Link href="/polls" className="btn-ghost inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Polls
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between mb-8 flex-wrap gap-4"
      >
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold">{poll.title}</h1>
            {getStatusBadge(poll.status)}
            {poll.publicResults && (
              <span className="flex items-center gap-1 text-xs text-dark-muted">
                <Eye className="w-3 h-3" /> Public
              </span>
            )}
          </div>
          <Markdown size="base" className="text-dark-muted">{poll.question}</Markdown>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              <button onClick={startEditing} className="btn btn-primary">
                Edit Poll
              </button>
              <button onClick={handleDelete} className="btn btn-ghost text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleSubmit(onSubmit)} 
                disabled={updatePoll.isPending}
                className="btn btn-primary"
              >
                <Save className="w-4 h-4 mr-2" />
                {updatePoll.isPending ? 'Saving...' : 'Save'}
              </button>
              <button onClick={cancelEditing} className="btn btn-ghost">
                Cancel
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* Quick Status Actions */}
      {!isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-2 mb-6 flex-wrap"
        >
          {poll.status !== 'active' && poll.status !== 'archived' && (
            <button
              onClick={() => handleStatusChange('active')}
              className="btn btn-ghost text-sm text-green-400"
            >
              <Check className="w-4 h-4 mr-1" /> Activate
            </button>
          )}
          {poll.status === 'active' && (
            <button
              onClick={() => handleStatusChange('closed')}
              className="btn btn-ghost text-sm text-yellow-400"
            >
              <X className="w-4 h-4 mr-1" /> Close
            </button>
          )}
          {poll.status === 'closed' && (
            <button
              onClick={() => handleStatusChange('draft')}
              className="btn btn-ghost text-sm"
            >
              Reset to Draft
            </button>
          )}
          {poll.status !== 'archived' && (
            <button
              onClick={() => handleStatusChange('archived')}
              className="btn btn-ghost text-sm text-purple-400"
            >
              <Archive className="w-4 h-4 mr-1" /> Archive
            </button>
          )}
          {poll.status === 'archived' && (
            <button
              onClick={() => handleStatusChange('draft')}
              className="btn btn-ghost text-sm text-purple-400"
            >
              <ArchiveRestore className="w-4 h-4 mr-1" /> Unarchive
            </button>
          )}
        </motion.div>
      )}

      {/* Edit Form */}
      {isEditing ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Title</label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  className="input"
                  placeholder="Poll title"
                />
              </div>
              <div>
                <label className="label">Status</label>
                <select {...register('status')} className="input">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className="label">Question</label>
              <MarkdownTextarea
                registration={register('question', { required: 'Question is required' })}
                value={watch('question')}
                onChange={v => setValue('question', v, { shouldDirty: true, shouldValidate: true })}
                placeholder="What would you like to ask?"
              />
            </div>

            {/* Answers */}
            <div>
              <label className="label">Answers (drag to reorder)</label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={answerFields.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {answerFields.map((field, index) => (
                      <SortableAnswerRow
                        key={field.id}
                        id={field.id}
                        index={index}
                        register={register}
                        canDelete={answerFields.length > 2}
                        onDelete={() => removeAnswer(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={() => appendAnswer({ text: '' })}
                className="btn btn-ghost mt-2 text-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Answer
              </button>
            </div>

            {/* Timing */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date (optional)</label>
                <input {...register('startsAt')} type="datetime-local" className="input" />
              </div>
              <div>
                <label className="label">End Date (optional)</label>
                <input {...register('endsAt')} type="datetime-local" className="input" />
              </div>
            </div>

            {/* Settings */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('publicResults')}
                  className="w-4 h-4 rounded border-dark-border bg-dark-card"
                />
                <span className="text-sm">Show results publicly</span>
              </label>
            </div>

            {/* Target Criteria */}
            <div className="border border-dark-border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Target Audience
                {targetPreviewCount !== null && (
                  <span className="ml-2 text-sm font-normal text-primary-400">
                    ({targetPreviewCount} users match)
                  </span>
                )}
                <button type="button" onClick={updatePreview} className="btn btn-ghost text-xs ml-auto">
                  Refresh count
                </button>
              </h3>

              <div className="space-y-4">
                {/* Subscription Status */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('subscribersOnly')} className="w-4 h-4" />
                    <span className="text-sm">Subscribers only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('notSubscribed')} className="w-4 h-4" />
                    <span className="text-sm">Non-subscribers only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('hasSpentMoney')} className="w-4 h-4" />
                    <span className="text-sm">Has spent money</span>
                  </label>
                </div>

                {/* Cities */}
                <div>
                  <label className="label text-sm">Filter by Cities</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setValue('cities', [])}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        !watch('cities')?.length ? 'bg-primary-500/20 border-primary-500' : 'border-dark-border'
                      }`}
                    >
                      🌍 All
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCitySearch(true)}
                      className="px-3 py-1 rounded-full text-sm border border-dashed border-dark-border flex items-center gap-1"
                    >
                      <Search className="w-3 h-3" /> Search
                    </button>
                  </div>
                  {watch('cities')?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {watch('cities').map((cityId) => (
                        <button
                          key={cityId}
                          type="button"
                          onClick={() => setValue('cities', watch('cities').filter(c => c !== cityId))}
                          className="px-2 py-0.5 rounded bg-primary-500/20 text-xs flex items-center gap-1 hover:bg-red-500/20"
                        >
                          {cities.find(c => c.id === cityId)?.name || cityId}
                          <X className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCityId}
                      onChange={(e) => setNewCityId(e.target.value)}
                      placeholder="Add city ID"
                      className="input flex-1 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCityId.trim()) {
                          e.preventDefault();
                          const current = watch('cities') || [];
                          if (!current.includes(newCityId.trim())) {
                            setValue('cities', [...current, newCityId.trim()]);
                          }
                          setNewCityId('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newCityId.trim()) {
                          const current = watch('cities') || [];
                          if (!current.includes(newCityId.trim())) {
                            setValue('cities', [...current, newCityId.trim()]);
                          }
                          setNewCityId('');
                        }
                      }}
                      className="btn btn-ghost"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Quiz Regex */}
                <div>
                  <label className="label text-sm">Filter by Quiz Answers</label>
                  {regexFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2 mb-2">
                      <input {...register(`quizAnswerRegex.${index}.field`)} className="input flex-1" placeholder="Field" />
                      <input {...register(`quizAnswerRegex.${index}.pattern`)} className="input flex-1" placeholder="Pattern" />
                      <button type="button" onClick={() => removeRegex(index)} className="btn btn-ghost text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => appendRegex({ field: '', pattern: '' })} className="btn btn-ghost text-sm">
                    <Plus className="w-4 h-4 mr-1" /> Add Filter
                  </button>
                </div>
              </div>
            </div>
          </form>
        </motion.div>
      ) : (
        /* Results View */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Results</h2>
            <span className="text-dark-muted">
              {poll.totalResponses} {poll.totalResponses === 1 ? 'response' : 'responses'}
            </span>
          </div>

          <div className="space-y-4">
            {poll.results.map((result) => (
              <div key={result.answerId}>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">{result.answerText}</span>
                  <span className="text-dark-muted">
                    {result.count} ({result.percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="h-3 bg-dark-border rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${result.percentage}%` }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="h-full bg-primary-500 rounded-full"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Target Criteria */}
          {poll.targetCriteria && Object.keys(poll.targetCriteria).length > 0 && (
            <div className="mt-6 pt-6 border-t border-dark-border">
              <h3 className="font-semibold mb-3 text-sm">Target Criteria</h3>
              <div className="flex flex-wrap gap-2">
                {poll.targetCriteria.subscribersOnly && (
                  <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Subscribers only</span>
                )}
                {poll.targetCriteria.notSubscribed && (
                  <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Non-subscribers</span>
                )}
                {poll.targetCriteria.hasSpentMoney && (
                  <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Has spent money</span>
                )}
                {poll.targetCriteria.cities?.map(cityId => (
                  <span key={cityId} className="px-2 py-1 text-xs rounded bg-primary-500/20">
                    {cities.find(c => c.id === cityId)?.name || cityId}
                  </span>
                ))}
                {poll.targetCriteria.quizAnswerRegex?.map((r, i) => (
                  <span key={i} className="px-2 py-1 text-xs rounded bg-primary-500/20">
                    {r.field}: /{r.pattern}/
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Timing Info */}
          {(poll.startsAt || poll.endsAt) && (
            <div className="mt-6 pt-6 border-t border-dark-border">
              <h3 className="font-semibold mb-3 text-sm">Timing</h3>
              <div className="flex flex-wrap gap-4 text-sm text-dark-muted">
                {poll.startsAt && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Starts: {new Date(poll.startsAt).toLocaleString()}
                  </span>
                )}
                {poll.endsAt && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Ends: {new Date(poll.endsAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* City Search Modal */}
      {showCitySearch && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => { setShowCitySearch(false); setCitySearchQuery(''); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card border border-content rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <MapPin className="w-5 h-5" /> Search Cities
              </h2>
              <button onClick={() => { setShowCitySearch(false); setCitySearchQuery(''); }} className="text-dark-muted hover:text-primary-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
              <input
                type="text"
                value={citySearchQuery}
                onChange={(e) => setCitySearchQuery(e.target.value)}
                placeholder="Search..."
                className="input pl-10 w-full"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {cities.map((city) => {
                const isSelected = watch('cities')?.includes(city.id);
                return (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => {
                      const current = watch('cities') || [];
                      setValue('cities', isSelected ? current.filter(c => c !== city.id) : [...current, city.id]);
                    }}
                    className={`w-full px-3 py-2 rounded-lg text-left flex items-center justify-between ${
                      isSelected ? 'bg-primary-500/20 border border-primary-500' : 'hover:bg-dark-card border border-transparent'
                    }`}
                  >
                    <div>
                      <span className="mr-2">{city.emoji}</span>
                      <span>{city.name}</span>
                      <span className="text-xs text-dark-muted ml-2">({city.id})</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-primary-500" />}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => { setShowCitySearch(false); setCitySearchQuery(''); }}
              className="btn btn-primary w-full mt-4"
            >
              Done
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

